import { EmptyState } from "@/components/common/EmptyState";
import { SeatStatusBadge } from "@/components/common/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import { RadarChart } from "@/components/progress/RadarChart";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useDateRange, useMemberProgress, useUsage } from "@/hooks";
import { formatSummary, projectionSpokes, spokesFromAxes } from "@/lib/radar";
import { cn } from "@/lib/utils";
import type { MemberActivity } from "@/types/api";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

const RANGES = [
	{ label: "7 days", days: 7 },
	{ label: "30 days", days: 30 },
	{ label: "90 days", days: 90 },
] as const;

/**
 * One person, in full: where their learning is, and how much of it there has
 * been.
 *
 * Two things sit side by side here, and they answer different questions. The
 * radar is SHAPE — which domains, and how far through them — and carries no
 * scores, because a tier means nothing until we explain it. The figures beside
 * it are EFFORT: time, tasks planned, tasks finished. An admin can read effort
 * without being invited to grade anybody.
 *
 * Reaching this page is audited server-side as `member.viewed`.
 */
export function AnalyticsMemberPage() {
	const { memberId = "" } = useParams();
	const [days, setDays] = useState<number>(30);
	// Stable across renders — see useDateRange.
	const range = useDateRange(days);

	const { data, isLoading } = useMemberProgress(memberId);
	const { data: usage, isLoading: usageLoading } = useUsage(range);

	const member = data?.member ?? null;
	const radar = data?.radar ?? null;
	const projection = radar?.projection ?? null;
	const activity =
		usage?.members.find((m) => m.user_id && m.user_id === member?.userId) ??
		null;
	const name = member?.name ?? member?.email ?? "This person";

	return (
		<>
			<Link
				to="/analytics"
				className="mb-3 inline-flex items-center gap-1.5 text-caption text-muted-foreground hover:text-heading"
			>
				<ArrowLeft className="h-3.5 w-3.5" />
				All analytics
			</Link>

			<PageHeader
				title={isLoading ? "…" : name}
				description={
					member?.name
						? member.email
						: "What they have been learning, and how much."
				}
				action={
					<div className="flex rounded-lg border border-divider bg-surface-primary p-0.5">
						{RANGES.map((r) => (
							<button
								key={r.days}
								type="button"
								onClick={() => setDays(r.days)}
								className={cn(
									"rounded-md px-3 py-1 text-caption transition-colors",
									days === r.days
										? "bg-surface3 font-medium text-heading"
										: "text-muted-foreground hover:text-heading",
								)}
							>
								{r.label}
							</button>
						))}
					</div>
				}
			/>

			{isLoading ? (
				<Skeleton className="h-96 w-full" />
			) : !member ? (
				<EmptyState
					title="Not found"
					description="This person is not on your team."
				/>
			) : (
				<>
					<Card className="mb-6">
						<CardContent className="p-5">
							<div className="mb-5 flex flex-wrap items-center gap-3">
								<SeatStatusBadge status={member.seatStatus} />
								<span className="text-caption text-muted-foreground">
									{member.role}
									{member.lastActiveAt
										? ` · last seen ${new Date(member.lastActiveAt).toLocaleDateString()}`
										: " · never seen"}
								</span>
							</div>

							{usageLoading ? (
								<Skeleton className="h-16 w-full" />
							) : (
								<div className="flex flex-wrap items-baseline gap-x-10 gap-y-4">
									<Stat
										label="Time spent"
										value={hours(activity?.minutes_spent ?? 0)}
									/>
									<Stat
										label="Tasks planned"
										value={activity?.tasks_planned ?? 0}
									/>
									<Stat
										label="Tasks finished"
										value={activity?.tasks_completed ?? 0}
										hint={completionHint(activity)}
									/>
									<Stat
										label="Tasks opened"
										value={activity?.tasks_engaged ?? 0}
									/>
									<Stat
										label="Days active"
										value={activity?.active_days ?? 0}
									/>
									<Stat
										label="Boards started"
										value={activity?.boards_started ?? 0}
									/>
									{activity?.streak_count ? (
										<Stat
											label="Streak"
											value={`${activity.streak_count} days`}
										/>
									) : null}
								</div>
							)}
						</CardContent>
					</Card>

					{!radar ||
					(radar.axes.length === 0 && (projection?.axes.length ?? 0) === 0) ? (
						<EmptyState
							title="Nothing learned yet"
							description="Their domains appear here once they open a reading, a podcast, a video or a whiteboard lesson."
						/>
					) : (
						<Card>
							<CardContent className="flex flex-col gap-8 p-5 md:flex-row">
								<div className="md:w-[360px] md:shrink-0">
									<RadarChart
										spokes={
											projection?.axes.length
												? projectionSpokes(projection.axes, radar.axes)
												: spokesFromAxes(radar.axes)
										}
										rings={radar.rings}
										size={360}
										title={`${name}'s domains`}
									/>
									{(projection?.additional.assigned ?? 0) > 0 ? (
										<p className="mt-1 text-center text-caption text-muted-foreground">
											Dashed orange: if {projection?.tasks.unfinished ?? 0}{" "}
											unfinished planned
											{projection?.tasks.unfinished === 1
												? " task was"
												: " tasks were"}{" "}
											completed
										</p>
									) : null}
									{radar.compareTo ? (
										<p className="mt-1 text-center text-caption text-muted-foreground">
											Dashed outline:{" "}
											{new Date(radar.compareTo).toLocaleDateString()}
										</p>
									) : null}
								</div>

								<div className="min-w-0 flex-1">
									<h2 className="mb-3 text-body font-semibold text-heading">
										Domains
									</h2>
									<table className="w-full text-body">
										<thead>
											<tr className="border-b border-divider text-caption uppercase tracking-wide text-muted-foreground">
												<th className="py-2 text-left font-medium">Domain</th>
												<th className="py-2 text-right font-medium">
													Activity
												</th>
											</tr>
										</thead>
										<tbody>
											{radar.axes.map((axis) => (
												<tr
													key={axis.key}
													className="border-b border-divider last:border-0"
												>
													{/* Whether a domain is one of their picks is deliberately
													    NOT shown. People change their domains freely, and they
													    search out boards well outside them — "not chosen" reads
													    as "shouldn't be here", which is neither true nor ours
													    to say. The picks only decide which axes get plotted. */}
													<td className="py-2.5 pr-4">
														<div className="text-heading">{axis.domain}</div>
														<div className="mt-0.5 text-caption text-muted-foreground">
															{formatSummary(axis.counts.byFormat)}
														</div>
													</td>
													<td className="tabular py-2.5 text-right align-top">
														<div className="text-paragraph">
															{axis.counts.pieces}{" "}
															{axis.counts.pieces === 1 ? "item" : "items"}
														</div>
														<div className="mt-0.5 text-caption text-muted-foreground">
															{axis.counts.finished} completed
														</div>
													</td>
												</tr>
											))}
										</tbody>
									</table>

									{activity?.topics?.length ? (
										<>
											<h2 className="mt-6 mb-2 text-body font-semibold text-heading">
												Boards they started
											</h2>
											<div className="flex flex-wrap gap-2">
												{activity.topics.slice(0, 12).map((topic) => (
													<span
														key={topic}
														className="inline-flex items-center rounded-full border border-divider bg-surface3 px-2.5 py-1 text-caption text-paragraph"
													>
														{topic}
													</span>
												))}
											</div>
										</>
									) : null}
								</div>
							</CardContent>
						</Card>
					)}

					<p className="mt-3 text-caption text-muted-foreground">
						Time and tasks cover the last {days} days. The radar covers
						everything they have done. Activity only — none of this says how
						well they know a subject.
					</p>
				</>
			)}
		</>
	);
}

/** "3h 20m", or "45m" when there is no hour to speak of. */
function hours(minutes: number): string {
	if (minutes < 60) return `${Math.round(minutes)}m`;
	const h = Math.floor(minutes / 60);
	const m = Math.round(minutes % 60);
	return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** "of 12 planned" — finished against asked, when there is anything to compare. */
function completionHint(activity: MemberActivity | null): string | undefined {
	if (!activity || activity.tasks_planned <= 0) return undefined;
	return `of ${activity.tasks_planned} planned`;
}

function Stat({
	label,
	value,
	hint,
}: {
	label: string;
	value: string | number;
	hint?: string;
}) {
	return (
		<div>
			<div className="tabular text-h3 font-semibold text-heading">{value}</div>
			<div className="text-caption text-muted-foreground">
				{label}
				{hint ? <span className="ml-1 text-muted2">{hint}</span> : null}
			</div>
		</div>
	);
}
